import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Cloud, Laptop, Music, Play } from "lucide-react";
import React from "react";
import { Track } from "../types";

interface RecentlyPlayedGridProps {
  tracks: Track[];
  onPlay: (track: Track) => void;
  hideLabels?: boolean;
}

export const RecentlyPlayedGrid: React.FC<RecentlyPlayedGridProps> = ({ tracks, onPlay, hideLabels = false }) => {
  const [startIndex, setStartIndex] = React.useState(0);
  const itemsPerPage = 6;

  const canScrollLeft = startIndex > 0;
  const canScrollRight = startIndex + itemsPerPage < tracks.length;

  const scrollLeft = () => {
    if (canScrollLeft) {
      setStartIndex((prev) => Math.max(0, prev - itemsPerPage));
    }
  };

  const scrollRight = () => {
    if (canScrollRight) {
      setStartIndex((prev) => Math.min(tracks.length - itemsPerPage, prev + itemsPerPage));
    }
  };

  const SourceIcon = ({ source }: { source: "spotify" | "local" }) => (
    <div
      className={`absolute top-2 right-2 p-1.5 rounded-full ${source === "spotify" ? "bg-[#1DB954]" : "bg-zinc-700"}`}
    >
      {source === "spotify" ? <Cloud className="w-3 h-3" /> : <Laptop className="w-3 h-3" />}
    </div>
  );

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">Jump Back In</h2>
          {!hideLabels && (
            <div className="flex items-center gap-4 text-sm text-zinc-400">
              <div className="flex items-center gap-1">
                <div className="p-1 bg-[#1DB954] rounded-full">
                  <Cloud className="w-3 h-3" />
                </div>
                <span>Spotify</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="p-1 bg-zinc-700 rounded-full">
                  <Laptop className="w-3 h-3" />
                </div>
                <span>Local</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className="p-2 hover:bg-zinc-800 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollRight}
            disabled={!canScrollRight}
            className="p-2 hover:bg-zinc-800 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {tracks.slice(startIndex, startIndex + itemsPerPage).map((track, index) => (
          <motion.div
            key={track.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative bg-zinc-800/50 p-4 rounded-lg hover:bg-zinc-800 transition"
          >
            <div className="relative">
              <button onClick={() => onPlay(track)} className="block w-full">
                {track.albumArt ? (
                  <img
                    src={track.albumArt}
                    alt={track.title}
                    className="w-full aspect-square object-cover rounded-md mb-3"
                  />
                ) : (
                  <div className="w-full aspect-square bg-zinc-700 rounded-md mb-3 flex items-center justify-center">
                    <Music className="w-8 h-8 text-zinc-500" />
                  </div>
                )}
                <SourceIcon source={track.source} />
              </button>
            </div>

            <div className="absolute inset-4 bottom-24 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
              <motion.button
                onClick={() => onPlay(track)}
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1.1 }}
                className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center"
              >
                <Play className="w-6 h-6" />
              </motion.button>
            </div>

            <div className="space-y-1">
              <a href={`/tracks/${track.id}`} className="font-medium truncate block hover:text-yellow-500 transition">
                {track.title}
              </a>
              <p className="text-sm text-zinc-400 truncate">{track.artist}</p>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span>{track.source === "spotify" ? "Spotify" : "Local"}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
